/*
 * Measurement Point Result History
 */

import * as React from 'react';
import * as moment from 'moment';

import { Button, Col, ListGroup, ListGroupItem, Row } from 'react-bootstrap';

import { IinstallBaseHistory } from '../../models';
import { TFunction } from 'i18next';
import {
    assetStatusToResultStatus,
    complianceStatusToResultStatus,
    measurementPointResultStatusTypesEnum
} from '../../models-enums';

/*
 * MMG-4338: both status columns reuse the status keys the app already translates and colours, so a
 * blank column stays genuinely blank rather than being filled with a status the row never recorded.
 */
const StatusCell = ({
    status,
    t
}: {
    status?: number | null;
    t: TFunction;
}) => {
    if (status === null || status === undefined) {
        return <span className="status-empty">-</span>;
    }

    const key = measurementPointResultStatusTypesEnum[status];

    return (
        <span className={`status ${key}`}>
            {t('manageMeasurementPointLists:' + key)}
        </span>
    );
};

/*
 * MeasurementPointListItem
 *
 */
const MeasurementPointListItem = ({
    result,
    setHistoricalResultID,
    toggleMPResultNotes,
    setSelectedWorkOrderID,
    toggleEditWorkOrderModal,
    t,
    getWorkOrderByID
}: {
    result: IinstallBaseHistory;
    setHistoricalResultID: (id: string) => void;
    toggleMPResultNotes: () => void;
    setSelectedWorkOrderID: (id: string) => void;
    toggleEditWorkOrderModal: () => void;
    t: TFunction;
    getWorkOrderByID: (id: string) => any;
}) => {
    return (
        <ListGroupItem
            key={result.id}
            onClick={() => {
                // If there is not a work order, show the notes, else show the work order
                if (result.workOrderId === undefined) {
                    setHistoricalResultID(result.id);
                    toggleMPResultNotes();
                } else {
                    setSelectedWorkOrderID(result.workOrderId);
                    getWorkOrderByID(result.workOrderId).then(() => {
                        toggleEditWorkOrderModal();
                    });
                }
            }}
        >
            <Row>
                <Col
                    md={3}
                    className="history-date"
                    style={{ paddingLeft: '4px', paddingRight: '2px' }}
                >
                    {moment
                        .utc(result.date)
                        .local(true)
                        .format('DD-MMM-YY LT')}
                </Col>
                <Col
                    md={3}
                    className="notes-column truncate"
                    style={{ paddingLeft: '4px', paddingRight: '2px' }}
                >
                    {result.jobType}
                </Col>
                <Col md={3} style={{ paddingLeft: '4px', paddingRight: '2px' }}>
                    <StatusCell
                        status={
                            result.assetStatus === null ||
                            result.assetStatus === undefined
                                ? null
                                : assetStatusToResultStatus[result.assetStatus]
                        }
                        t={t}
                    />
                </Col>
                <Col
                    md={3}
                    style={{ paddingLeft: '4px', paddingRight: '10px' }}
                >
                    <StatusCell
                        status={
                            result.complianceStatus === null ||
                            result.complianceStatus === undefined
                                ? null
                                : complianceStatusToResultStatus[
                                      result.complianceStatus
                                  ]
                        }
                        t={t}
                    />
                </Col>
            </Row>
        </ListGroupItem>
    );
};

interface Iprops {
    isLoading: boolean;
    installBaseHistory: IinstallBaseHistory[];
    t: TFunction;
    locationString: string;
    colorButton: any;
    toggleModal: () => void;
    toggleMPResultNotes: () => void;
    toggleWorkOrderCloseModal: () => void;
    setHistoricalResultID: (id: string) => void;
    setSelectedWorkOrderID: (id: string) => void;
    toggleEditWorkOrderModal: () => void;
    getWorkOrderByID: (id: string) => any;
}
export const MPResultHistory = (props: Iprops) => {
    const { t, installBaseHistory, isLoading } = props;
    if (installBaseHistory.length) {
        return (
            <div className="result-history">
                {/* an empty location string used to leave a tall blank gap above the headers */}
                {props.locationString ? (
                    <h5 style={{ paddingLeft: '15px', paddingBottom: '15px' }}>
                        {props.locationString}
                    </h5>
                ) : null}
                {/* margin/padding mirror the ListGroupItem rows below so columns line up */}
                <Row className="result-history-header" style={{ margin: 0 }}>
                    <Col
                        md={3}
                        style={{ paddingLeft: '4px', paddingRight: '2px' }}
                    >
                        {t('manageMeasurementPointLists:historyDate')}
                    </Col>
                    <Col
                        md={3}
                        style={{ paddingLeft: '4px', paddingRight: '2px' }}
                    >
                        {t('manageMeasurementPointLists:historyActivity')}
                    </Col>
                    <Col
                        md={3}
                        style={{ paddingLeft: '4px', paddingRight: '2px' }}
                    >
                        {t('manageMeasurementPointLists:historyAssetStatus')}
                    </Col>
                    <Col
                        md={3}
                        style={{ paddingLeft: '4px', paddingRight: '10px' }}
                    >
                        {t(
                            'manageMeasurementPointLists:historyComplianceStatus'
                        )}
                    </Col>
                </Row>
                <ListGroup>
                    {installBaseHistory.map(result => {
                        return (
                            <MeasurementPointListItem
                                key={result.id}
                                {...props}
                                result={result}
                            />
                        );
                    })}
                </ListGroup>
                <Col xs={12} className="form-buttons text-right">
                    <Button
                        bsStyle={props.colorButton}
                        type="button"
                        onClick={props.toggleModal}
                    >
                        {t('common:doneButton')}
                    </Button>
                </Col>
            </div>
        );
    } else {
        return (
            <div className="result-history">
                <Col md={12}>
                    {isLoading ? (
                        <h4>{t('common:loading')}</h4>
                    ) : (
                        <h4>
                            {t('manageMeasurementPointLists:noAssetHistory')}
                        </h4>
                    )}
                </Col>
                <Col xs={12} className="form-buttons text-right">
                    <Button
                        bsStyle={props.colorButton}
                        type="button"
                        onClick={props.toggleModal}
                    >
                        {t('common:doneButton')}
                    </Button>
                </Col>
            </div>
        );
    }
};
